// Drives the six single-character boxes that stand in for one 6-digit code field: typing advances,
// backspace retreats, and a paste fills them all. The combined value is mirrored into the form's
// hidden Code field, which is what actually posts, and the form submits itself once complete.
function initCodeBoxes(): void {
    const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('.code-input'));
    if (inputs.length === 0) {
        return;
    }

    const field = document.getElementById('Code') as HTMLInputElement | null;
    const form = inputs[0].form;
    if (!field || !form) {
        return;
    }

    const group = document.getElementById('CodeInputGroup');

    const sync = (): void => {
        field.value = inputs.map(i => i.value).join('');
    };

    const submitWhenComplete = (): void => {
        if (field.value.length === inputs.length) {
            group?.classList.add('code-complete');
            form.requestSubmit();
        }
    };

    inputs.forEach((input, index) => {
        // Selecting the digit lets Backspace delete it or typing replace it, even after Safari moves the cursor following a click.
        input.addEventListener('focus', () => input.select());
        input.addEventListener('click', () => window.setTimeout(() => {
            if (document.activeElement === input) {
                input.select();
            }
        }));

        input.addEventListener('input', () => {
            input.value = input.value.replace(/[^0-9]/g, '').slice(0, 1);
            input.classList.toggle('filled', input.value !== '');
            sync();
            if (!input.value) {
                return;
            }
            if (index < inputs.length - 1) {
                inputs[index + 1].focus();
            } else {
                submitWhenComplete();
            }
        });

        input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Backspace' && !input.value && index > 0) {
                inputs[index - 1].focus();
            }
        });

        input.addEventListener('paste', (e: ClipboardEvent) => {
            e.preventDefault();
            const digits = (e.clipboardData?.getData('text') ?? '').replace(/[^0-9]/g, '');
            for (let i = 0; i < digits.length && i < inputs.length; i++) {
                inputs[i].value = digits[i];
                inputs[i].classList.add('filled');
            }
            sync();
            if (digits.length > 0) {
                inputs[Math.min(digits.length, inputs.length - 1)].focus();
            }
            submitWhenComplete();
        });
    });

    inputs[0].focus();
}

// The resend button remains disabled until the countdown ends, preventing repeated code requests in a loop.
// The countdown uses a fixed deadline calculated from Date.now() at page load and remains correct after
// tab suspension, background timer throttling, and a back/forward-cache restore.
function initResendCountdown(): void {
    const button = document.getElementById('ResendButton') as HTMLButtonElement | null;
    const countdown = document.getElementById('Countdown');
    if (!button || !countdown) {
        return;
    }

    const resendAllowedAt = Date.now() + Number(button.dataset.cooldownSeconds ?? '0') * 1000;
    let timer: number | undefined;

    const render = (): void => {
        window.clearTimeout(timer);
        const remainingMilliseconds = resendAllowedAt - Date.now();
        if (remainingMilliseconds > 0) {
            countdown.textContent = `(${Math.ceil(remainingMilliseconds / 1000)}s)`;
            button.disabled = true;
            timer = window.setTimeout(render, remainingMilliseconds % 1000 || 1000);
            return;
        }
        countdown.textContent = '';
        button.disabled = false;
    };

    render();

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) {
            // FormSubmitGuard.handlePageRestore re-enables every submit button on pageshow, including
            // ResendButton, and can run after this listener. Deferring the render keeps the button disabled
            // while time remains.
            window.setTimeout(render, 0);
        }
    });

    // Browsers pause timers in hidden tabs. Rendering again when the tab becomes visible updates
    // the countdown and button immediately.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            render();
        }
    });
}

// When the server re-renders the code screen after a wrong code, the group carries
// data-has-error="true". Shake the boxes once, then clear them and refocus the first
// so the user can retype immediately.
function initCodeError(): void {
    const group = document.getElementById('CodeInputGroup');
    if (!group || group.dataset.hasError !== 'true') {
        return;
    }

    const inputs = Array.from(group.querySelectorAll<HTMLInputElement>('.code-input'));
    const field = document.getElementById('Code') as HTMLInputElement | null;

    const clearAndRefocus = (): void => {
        inputs.forEach(i => { i.value = ''; i.classList.remove('filled'); });
        if (field) {
            field.value = '';
        }
        inputs[0]?.focus();
    };

    // The shake animation is disabled under prefers-reduced-motion, so its
    // animationend never fires; clear synchronously in that case instead.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        clearAndRefocus();
        return;
    }

    group.classList.add('code-shake');
    group.addEventListener('animationend', () => {
        group.classList.remove('code-shake');
        clearAndRefocus();
    }, { once: true });
}

document.addEventListener('DOMContentLoaded', () => {
    initCodeBoxes();
    initResendCountdown();
    initCodeError();
});
